// חישובי יעדים תזונתיים — לוגיקה טהורה בלבד, בלי DOM ובלי רשת.

export type Sex = 'female' | 'male';
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'high';

export interface BmrInput {
  sex: Sex;
  weightKg: number;
  heightCm: number;
  age: number;
}

/** הנתונים הדרושים לחישוב היעדים (תת-קבוצה של litebite_profiles). */
export interface GoalsInput {
  sex: Sex;
  /** תאריך לידה בפורמט YYYY-MM-DD */
  birthdate: string;
  heightCm: number;
  currentWeightKg: number;
  goalWeightKg: number;
  activityLevel: ActivityLevel;
  /** קצב ירידה רצוי בק"ג לשבוע (0.25 / 0.5 / 0.75) */
  weeklyRateKg: number;
}

export interface Targets {
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  /** true כשהיעד הוגבל לרצפת הבטיחות — ה-UI מסביר שהקצב בפועל יהיה איטי יותר */
  clamped: boolean;
}

/** מקדמי פעילות (TDEE = BMR × מקדם) */
const ACTIVITY_FACTOR: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
};

/** רצפת בטיחות קלורית יומית — לא יורדים מתחתיה */
export const CALORIE_FLOOR: Record<Sex, number> = { female: 1200, male: 1500 };

/** קק"ל בק"ג רקמת שומן — לגזירת הגירעון היומי מקצב הירידה */
const KCAL_PER_KG = 7700;

/** BMR לפי Mifflin-St Jeor: ‏10×משקל + 6.25×גובה − 5×גיל, ‏+5 לזכר / ‎−161 לנקבה. */
export function calcBmr({ sex, weightKg, heightCm, age }: BmrInput): number {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return sex === 'male' ? base + 5 : base - 161;
}

/** סך ההוצאה היומית — BMR כפול מקדם הפעילות. */
export function calcTdee(bmr: number, activityLevel: ActivityLevel): number {
  return bmr * ACTIVITY_FACTOR[activityLevel];
}

/** גיל בשנים שלמות מתאריך לידה (YYYY-MM-DD). פירוק ידני — new Date על המחרוזת מפרש כ-UTC וגורם לסטיות יום. מחזיר NaN לקלט לא תקין. */
export function calcAge(birthdate: string): number {
  const [y, m, d] = birthdate.split('-').map(Number);
  if (!y || !m || !d) return NaN;
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age--;
  return age;
}

/**
 * יעדים יומיים מהפרופיל:
 * קלוריות = TDEE פחות גירעון (קצב × 7700 / 7), עם רצפת בטיחות לפי מין.
 * חלבון 1.8 ג' × משקל היעד; שומן 30% מהקלוריות (÷9); פחמימות — היתרה (÷4).
 */
export function calcTargets(input: GoalsInput): Targets {
  const age = calcAge(input.birthdate);
  const bmr = calcBmr({
    sex: input.sex,
    weightKg: input.currentWeightKg,
    heightCm: input.heightCm,
    age,
  });
  const tdee = calcTdee(bmr, input.activityLevel);
  const dailyDeficit = (input.weeklyRateKg * KCAL_PER_KG) / 7;

  let calories = Math.round(tdee - dailyDeficit);
  const floor = CALORIE_FLOOR[input.sex];
  const clamped = calories < floor;
  if (clamped) calories = floor;

  const proteinG = Math.round(1.8 * input.goalWeightKg);
  const { fatG, carbsG } = deriveFatCarbs(calories, proteinG);

  return { calories, proteinG, carbsG, fatG, clamped };
}

/** שומן = 30% מהקלוריות, פחמימות = היתרה (לא שליליות גם במקרה קצה). meta משותף
 *  ל-calcTargets ולהצעת היעד המותאם (computeAdaptiveTarget) — שניהם באותו יחס. */
function deriveFatCarbs(calories: number, proteinG: number): { fatG: number; carbsG: number } {
  const fatG = Math.round((calories * 0.3) / 9);
  const carbsG = Math.max(0, Math.round((calories - proteinG * 4 - fatG * 9) / 4));
  return { fatG, carbsG };
}

// ---------------------------------------------------------------- יעד מותאם (לפי משקל בפועל)

export interface AdaptiveTargetInput {
  /** צריכת אחזקה אמיתית משוערת, קק"ל/יום — כבר מחושבת ע"י הקורא (ראה progress.ts:
   *  impliedMaintenance = avgCalories + שינוי-משקל-בפועל×7700/ימי-התקופה). כך גם התצוגה
   *  בדוח וגם ההצעה כאן משתמשות באותו מקור-אמת יחיד, ללא תלות בתקופה קבועה מראש. */
  impliedMaintenance: number;
  /** קצב הירידה הרצוי, ק"ג/שבוע (מהפרופיל) */
  desiredWeeklyRateKg: number;
  /** לחישוב רצפת הבטיחות האישית (BMR מדויק, לא רק סף שטוח לפי מין) */
  bmr: BmrInput;
  proteinG: number;
}

export interface AdaptiveTargetResult {
  /** היעד המוצע (אחזקה בפועל פחות הגירעון הרצוי), אחרי רצפת בטיחות */
  suggestedCalories: number;
  suggestedProteinG: number;
  suggestedCarbsG: number;
  suggestedFatG: number;
  /** הרצפה שחושבה: המקסימום בין הסף השטוח לפי מין לבין ה-BMR האישי */
  floor: number;
  /** true אם ההצעה הוגבלה ע"י הרצפה */
  clamped: boolean;
}

/**
 * יעד מותאם אישית לפי אחזקה אמיתית (לא רק חישוב תיאורטי): מאפשר לגלות שהאחזקה בפועל
 * שונה מהתחזית (TDEE מפורמולה). רצפת בטיחות = לא פחות מ-BMR (ולא פחות מהסף השטוח לפי מין)
 * — עוד יותר מגן ממי שקטן/ה בגובה ובמשקל שהסף השטוח לבדו לא היה שומר עליה/ו.
 */
export function computeAdaptiveTarget(input: AdaptiveTargetInput): AdaptiveTargetResult {
  const desiredDailyDeficit = (input.desiredWeeklyRateKg * KCAL_PER_KG) / 7;

  const bmr = calcBmr(input.bmr);
  const floor = Math.max(CALORIE_FLOOR[input.bmr.sex], Math.round(bmr));

  let suggestedCalories = Math.round(input.impliedMaintenance - desiredDailyDeficit);
  const clamped = suggestedCalories < floor;
  if (clamped) suggestedCalories = floor;

  const { fatG, carbsG } = deriveFatCarbs(suggestedCalories, input.proteinG);
  return {
    suggestedCalories,
    suggestedProteinG: input.proteinG, suggestedCarbsG: carbsG, suggestedFatG: fatG,
    floor, clamped,
  };
}
